import "../styles/hr.css";
import {
  hrStempelStart, hrStempelPause, hrStempelResume, hrStempelStop, hrStempelStatus,
} from "../api/hr";
import { ApiError } from "../api/client";
import { toastApiError, pushToast } from "../api/errors";
import { navigate } from "../router/router";

/* Topbar-Stempeluhr — App-Shell-Komponente (kein RouteHandler).
 * Ein einzelnes Live-Widget rechts in der Topbar: spiegelt hr/stempel/status,
 * pollt alle 30s und zählt dazwischen lokal hoch. Buttons → start|pause|resume|stop.
 * Markup nutzt die .tbw-Klassen aus styles/hr.css (1:1 aus dem Design).
 */

type Stamp = {
  running: boolean;
  kind: "aktiv" | "pause" | null;
  startTs: number;
  pauseSummeSek: number;
  fetchedNettoSec: number;
  ort: string;
  notiz: string;
};

let state: Stamp = { running: false, kind: null, startTs: 0, pauseSummeSek: 0, fetchedNettoSec: 0, ort: "", notiz: "" };
let pendingTaetigkeit = ""; // im Popover getippte Tätigkeit (überlebt Re-Renders/Poll)
let pollTimer = 0;
let tickTimer = 0;
let host: HTMLElement | null = null;
let available = true; // false, sobald status dauerhaft 403 → Widget ausblenden (kein Zeiterfassungs-Profil)
let popOpen = false;
let outsideBound = false;

export function mountTopbarStempel(slot: HTMLElement): void {
  host = slot;
  render();
  void loadStatus();
  if (!pollTimer) pollTimer = window.setInterval(() => void loadStatus(), 30_000);
  if (!tickTimer) tickTimer = window.setInterval(tick, 1000);
}

/** Erlaubt der Stunden-View, nach eigenen Stempel-Aktionen den Status zu refreshen. */
export function refreshTopbarStempel(): void {
  void loadStatus();
}

function lucide(): void {
  (window as unknown as { lucide?: { createIcons?: (o: Record<string, unknown>) => void } }).lucide?.createIcons?.({ nameAttr: "data-l" });
}

async function loadStatus(): Promise<void> {
  try {
    const s = await hrStempelStatus();
    if (s.running) {
      const nowS = Math.floor(Date.now() / 1000);
      state = {
        running: true,
        kind: s.status,
        startTs: s.start_ts,
        pauseSummeSek: s.pause_summe_sek,
        fetchedNettoSec: Math.max(0, nowS - s.start_ts - s.pause_summe_sek),
        ort: s.ort ?? "",
        notiz: s.notiz ?? "",
      };
    } else {
      state = { running: false, kind: null, startTs: 0, pauseSummeSek: 0, fetchedNettoSec: 0, ort: "", notiz: "" };
    }
    available = true;
  } catch (err) {
    // 403 = kein Zeiterfassungs-Profil → Widget dezent ausblenden, nicht toasten.
    if (err instanceof ApiError && err.statusCode === 403) available = false;
  }
  render();
}

function nettoSec(): number {
  if (!state.running) return 0;
  return state.kind === "aktiv"
    ? Math.max(0, Math.floor(Date.now() / 1000) - state.startTs - state.pauseSummeSek)
    : state.fetchedNettoSec;
}

function tick(): void {
  if (!host || !state.running) return;
  const t = host.querySelector(".tbw-timer");
  if (t) t.textContent = fmtHm(nettoSec());
  const pt = host.querySelector(".tbw-pop-timer");
  if (pt) pt.textContent = fmtHms(nettoSec());
}

function render(): void {
  if (!host) return;
  if (!available) { host.innerHTML = ""; return; }
  const dotCls = !state.running ? "is-off" : state.kind === "pause" ? "is-pause" : "is-run";
  const stateLbl = !state.running ? "Nicht gestempelt" : state.kind === "pause" ? "Pause" : "Läuft";
  const ctrls = !state.running
    ? `<button class="tbw-btn tbw-btn-go" data-act="start" title="Start"><svg data-l="play"></svg></button>`
    : state.kind === "pause"
      ? `<button class="tbw-btn tbw-btn-go" data-act="resume" title="Weiter"><svg data-l="play"></svg></button><button class="tbw-btn tbw-btn-stop" data-act="stop" title="Stopp"><svg data-l="square"></svg></button>`
      : `<button class="tbw-btn" data-act="pause" title="Pause"><svg data-l="pause"></svg></button><button class="tbw-btn tbw-btn-stop" data-act="stop" title="Stopp"><svg data-l="square"></svg></button>`;
  host.innerHTML = `<div class="tbw-wrap">
    <div class="tbw ${state.running ? "is-active" : ""}" data-toggle title="Zeiterfassung — klick für Details">
      <span class="tbw-dot ${dotCls}"></span>
      <div class="tbw-meta">
        <span class="tbw-timer">${state.running ? fmtHm(nettoSec()) : "00:00"}</span>
        <span class="tbw-state">${stateLbl}</span>
      </div>
      <div class="tbw-ctrls">${ctrls}</div>
    </div>
    ${popOpen ? popoverHtml() : ""}
  </div>`;
  host.querySelectorAll<HTMLButtonElement>("[data-act]").forEach((b) =>
    b.addEventListener("click", (e) => { e.stopPropagation(); void act(b.dataset.act as "start" | "pause" | "resume" | "stop"); }));
  host.querySelector<HTMLElement>("[data-toggle]")?.addEventListener("click", (e) => { e.stopPropagation(); popOpen = !popOpen; render(); });
  host.querySelector<HTMLElement>("[data-pop-stunden]")?.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); popOpen = false; void navigate("/stunden"); render(); });
  const tin = host.querySelector<HTMLInputElement>("#tbw-taetigkeit");
  if (tin) {
    tin.addEventListener("mousedown", (e) => e.stopPropagation());
    tin.addEventListener("click", (e) => e.stopPropagation());
    tin.addEventListener("input", () => { pendingTaetigkeit = tin.value; });
  }
  lucide();
  bindOutside();
}

function popoverHtml(): string {
  const dotCls = !state.running ? "is-off" : state.kind === "pause" ? "is-pause" : "is-run";
  const stateLbl = !state.running ? "Nicht gestempelt" : state.kind === "pause" ? "Pausiert" : `Läuft seit ${berlinTime(state.startTs)}`;
  const ctrls = !state.running
    ? `<button class="hr-btn hr-btn-primary" data-act="start" style="flex:1;justify-content:center"><svg data-l="play" style="width:15px;height:15px"></svg> Start</button>`
    : state.kind === "pause"
      ? `<button class="hr-btn hr-btn-primary" data-act="resume" style="flex:1;justify-content:center"><svg data-l="play" style="width:15px;height:15px"></svg> Weiter</button><button class="hr-btn hr-btn-stop" data-act="stop" style="flex:1;justify-content:center"><svg data-l="square" style="width:13px;height:13px"></svg> Stopp</button>`
      : `<button class="hr-btn hr-btn-secondary" data-act="pause" style="flex:1;justify-content:center"><svg data-l="pause" style="width:15px;height:15px"></svg> Pause</button><button class="hr-btn hr-btn-stop" data-act="stop" style="flex:1;justify-content:center"><svg data-l="square" style="width:13px;height:13px"></svg> Stopp</button>`;
  const meta = state.running
    ? `<div class="tbw-pop-meta">${state.ort ? `<div><svg data-l="map-pin" style="width:12px;height:12px"></svg> ${esc(state.ort)}</div>` : ""}<div><svg data-l="coffee" style="width:12px;height:12px"></svg> Pause ${fmtHm(state.pauseSummeSek)}</div></div>`
    : "";
  return `<div class="tbw-pop">
    <div class="tbw-pop-h">
      <div>
        <div class="tbw-pop-state"><span class="tbw-dot ${dotCls}"></span>${esc(stateLbl)}</div>
        <div class="tbw-pop-timer">${state.running ? fmtHms(nettoSec()) : "00:00:00"}</div>
      </div>
      ${meta}
    </div>
    <input type="text" class="hr-input" id="tbw-taetigkeit" placeholder="Tätigkeit — was machst du?" value="${esc(pendingTaetigkeit || state.notiz)}" style="width:100%;margin:4px 0 8px;font-size:12.5px" />
    <div class="tbw-pop-ctrls">${ctrls}</div>
    <div class="tbw-pop-foot">
      <span>Zeiterfassung</span>
      <a href="/stunden" data-pop-stunden>Zur Stunden-Ansicht →</a>
    </div>
  </div>`;
}

function bindOutside(): void {
  if (outsideBound) return;
  outsideBound = true;
  document.addEventListener("click", () => {
    if (!popOpen) return;
    // Klicks im Widget rufen stopPropagation → erreichen dieses Handler nicht.
    popOpen = false; render();
  });
}

async function act(kind: "start" | "pause" | "resume" | "stop"): Promise<void> {
  try {
    const taet = pendingTaetigkeit.trim() || undefined;
    if (kind === "start") await hrStempelStart(taet ? { notiz: taet } : {});
    else if (kind === "pause") await hrStempelPause();
    else if (kind === "resume") await hrStempelResume();
    else if (kind === "stop") {
      const r = await hrStempelStop(taet ? { taetigkeit: taet } : {});
      pendingTaetigkeit = "";
      pushToast("Gestoppt", `${r.datum}: ${r.dauer_netto} erfasst.`, "success");
      if (r.pauseDefizitMin > 0 || r.ueber10h) {
        const hints = [r.pauseDefizitMin > 0 ? `Pause ${r.pauseDefizitMin} min unter Mindestmaß` : "", r.ueber10h ? "über 10 Std/Tag" : ""].filter(Boolean).join(" · ");
        pushToast("Hinweis (ArbZG)", `${hints} — bitte ggf. korrigieren.`, "warning");
      }
    }
    await loadStatus();
    window.dispatchEvent(new CustomEvent("hr:stempel-changed")); // Stunden-View refreshen
  } catch (err) {
    if (err instanceof ApiError) toastApiError(err);
  }
}

function fmtHm(sec: number): string {
  const m = Math.round(sec / 60);
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}
function fmtHms(sec: number): string { const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = Math.floor(sec % 60); return `${pad(h)}:${pad(m)}:${pad(s)}`; }
function berlinTime(ts: number): string { try { return new Date(ts * 1000).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }); } catch { return ""; } }
function esc(s: string): string { return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"); }
function pad(n: number): string { return String(n).padStart(2, "0"); }
